import { Component, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';

import { Question, Questionnaire, QuestionOption, QuestionnaireSubmitPayload, UserInfoField } from '@app/core/models/questionnaire.model';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import {
  QuestionSubType,
  QuestionType,
} from '@app/core/enums/questionnaire.enums';
import { EMAIL_FIELD_VALIDATORS } from '@core/constants/patterns.constant';
import { CheckBox } from '@app/shared/components/questions/templates/choice-based/check-box/check-box';
import { RadioButton } from '@app/shared/components/questions/templates/choice-based/radio-button/radio-button';
import { SingleSelect } from '@app/shared/components/questions/templates/choice-based/single-select/single-select';
import { MultiSelect } from '@app/shared/components/questions/templates/choice-based/multi-select/multi-select';
import { ButtonSelectOptions } from '@app/shared/components/questions/templates/choice-based/button-select-options/button-select-options';
import { ShortText } from '@app/shared/components/questions/templates/text-input/short-text/short-text';
import { LongText } from '@app/shared/components/questions/templates/text-input/long-text/long-text';
import { Mobile } from '@app/shared/components/questions/templates/text-input/mobile/mobile';
import { RatingScale } from '@app/shared/components/questions/templates/rating-scale/rating-scale/rating-scale';
import { OpinionScale } from '@app/shared/components/questions/templates/rating-scale/opinion-scale/opinion-scale';
import { Nps } from '@app/shared/components/questions/templates/nps/nps/nps';
import { Slider } from '@app/shared/components/questions/templates/slider/slider/slider';
import { RankingOrder } from '@app/shared/components/questions/templates/ranking-order/ranking-order/ranking-order';
import { SocialReview } from '@app/shared/components/questions/templates/social-review/social-review/social-review';
import { ASSETS } from '@app/core/constants/assets.constants';
import { Spinner } from '@app/shared/components/spinner/spinner';

@Component({
  selector: 'app-reusable-question-components',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    CheckBox,
    RadioButton,
    SingleSelect,
    MultiSelect,
    ButtonSelectOptions,

    ShortText,
    LongText,
    Mobile,
    RatingScale,
    OpinionScale,
    Nps,
    Slider,
    RankingOrder,
    SocialReview,
    Spinner,
  ],
  templateUrl: './reusable-question-components.html',
  styleUrl: './reusable-question-components.scss',
})
export class ReusableQuestionComponents {
  @Input() organizationId: string = '';
  @Input() questionnaireId: string = '';
  @Input() questionnaire: Questionnaire | null = null;
  @Input() isSubmitting: boolean = false;
  @Output() onSubmitAnswers = new EventEmitter<QuestionnaireSubmitPayload>();
  /** Emits the 1-based current page index when it changes (for parent to sync e.g. page section). */
  @Output() currentPageChange = new EventEmitter<number>();
  /** When true, welcome screen is not rendered (handled by parent). Treated as already shown. */
  @Input() skipWelcomeScreen: boolean = false;
  /** When true, duplicate response block is not rendered (handled by parent). */
  @Input() hideDuplicateResponse: boolean = false;
  ASSETS = ASSETS;
  private destroy$ = new Subject<void>();
  questionAnswers: { [questionId: string]: any } = {};
  userInfoAnswers: { [key: string]: any } = {};
  formErrors: { [key: string]: string } = {};
  showValidationErrors = false;
  userInfoValidationErrors: { [key: string]: string } = {};
  showUserInfoValidationErrors = false;
  userInfoSubmitted = false;
  welcomeScreenShown = false; // Track if welcome screen has been shown
  // Form and validation (initialized empty so template never sees undefined)
  previewForm = new FormGroup({});
  userInfoForm = new FormGroup({});

  quectionTypes = QuestionType;
  questionSubTypes = QuestionSubType;
  publicQuestions: Question[] | undefined = [];
  userInfoFields: UserInfoField[] | undefined = [];

  // Pagination properties
  currentPage: number = 1;
  totalPages: number = 1;
  currentPageQuestions: Question[] = [];

  ngOnInit(): void {
    this.initializeFromQuestionnaire();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['questionnaire'] && this.questionnaire) {
      this.initializeFromQuestionnaire();
    }
  }

  private initializeFromQuestionnaire(): void {
    if (!this.questionnaire) return;
    this.publicQuestions = this.questionnaire.questions as unknown as Question[];
    this.userInfoFields = this.questionnaire.userInfoCaptureSettings?.userInfoFields ?? [];
    this.initializeForms();
    this.initializePagination();
    this.welcomeScreenShown = this.skipWelcomeScreen || !this.hasWelcomeScreen();
    const hasUserInfo = this.userInfoFields && this.userInfoFields.length > 0;
    const statusCondition = this.questionnaire.userInfoCaptureSettings?.statusCondition;
    // Show user info when we have fields and status is MANDATORY or OPTIONAL; otherwise go to questions
    this.userInfoSubmitted = !(
      hasUserInfo &&
      (statusCondition === 'MANDATORY' || statusCondition === 'OPTIONAL')
    );
  }

  onStartQuestionnaire(): void {
    // Hide welcome screen and proceed to next step
    this.welcomeScreenShown = true;

    // Check status condition to determine next step
    const statusCondition = this.questionnaire?.userInfoCaptureSettings?.statusCondition;

    if (statusCondition === 'ANONYMOUS') {
      // Skip user info, go directly to questions
      this.userInfoSubmitted = true;
    } else if (statusCondition === 'OPTIONAL' || statusCondition === 'MANDATORY') {
      // Show user info form
      this.userInfoSubmitted = false;
    } else {
      // Default: skip user info
      this.userInfoSubmitted = true;
    }
  }

  onSubmitUserInfo() {
    if (!this.validateUserInfoForm()) {
      return;
    }

    // Store user info answers
    this.userInfoFields?.forEach((field) => {
      const control = this.userInfoForm.get(field.fieldName);
      if (control) {
        this.userInfoAnswers[field.fieldName] = {
          fieldName: field.fieldName,
          fieldType: field.fieldType,
          fieldValue: control.value || '',
          fieldLength: field.fieldLength || 0,
          keyIdentifierFlag: field.keyIdentifierFlag || false,
          mandatoryFlag: field.mandatoryFlag || false
        };
      }
    });

    // Mark user info as submitted and show questions
    this.userInfoSubmitted = true;
  }

  onSubmit() {
    // Validate all pages before submitting
    if (!this.validateAllPages()) {
      // If validation fails, navigate to the first page with errors
      this.navigateToFirstErrorPage();
      return;
    }

    // Build user info array from stored answers
    const userInfo = Object.values(this.userInfoAnswers).map((answer: any) => ({
      fieldName: answer.fieldName,
      fieldType: answer.fieldType,
      fieldValue: answer.fieldValue,
      fieldLength: answer.fieldLength,
      keyIdentifierFlag: answer.keyIdentifierFlag,
      mandatoryFlag: answer.mandatoryFlag
    }));

    // Extract user details for payload
    // const nameField = userInfo.find((info: any) => info.fieldType === 'NAME');
    // const emailField = userInfo.find((info: any) => info.fieldType === 'EMAIL');
    // const phoneField = userInfo.find((info: any) => info.fieldType === 'PHONE');

    const payload = {
      customerId: this.organizationId,
      questionnaireId: this.questionnaireId,
      userId: '',
      // userEmail: emailField?.fieldValue || '',
      // userName: nameField?.fieldValue || '',
      userInfo: userInfo,
      responses: Object.values(this.questionAnswers).filter((response) => {
        if (response.textResponse !== undefined) {
          return response.textResponse && response.textResponse.trim().length > 0;
        } else if (response.score !== undefined) {
          return response.score !== null && response.score !== undefined;
        } else if (response.options !== undefined) {
          return Array.isArray(response.options) && response.options.length > 0;
        }
        return false;
      }),
    };
    this.onSubmitAnswers.emit(payload as unknown as QuestionnaireSubmitPayload);
  }



  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializePagination() {
    if (!this.publicQuestions || this.publicQuestions.length === 0) {
      this.totalPages = 1;
      this.currentPage = 1;
      this.currentPageQuestions = [];
      return;
    }

    // Normalize page numbers - ensure all questions have a pageNumber (default to 1)
    this.publicQuestions.forEach(q => {
      if (!q.pageNumber || q.pageNumber < 1) {
        q.pageNumber = 1;
      }
    });

    // Get unique page numbers from questions
    const pageNumbers = [...new Set(this.publicQuestions.map(q => q.pageNumber))].sort((a, b) => a - b);
    this.totalPages = pageNumbers.length > 0 ? pageNumbers.length : 1;
    this.currentPage = 1;
    this.updateCurrentPageQuestions();
  }

  private updateCurrentPageQuestions() {
    if (!this.publicQuestions) {
      this.currentPageQuestions = [];
      return;
    }

    // Get unique page numbers sorted
    const pageNumbers = [...new Set(this.publicQuestions.map(q => q.pageNumber))].sort((a, b) => a - b);

    if (pageNumbers.length === 0) {
      this.currentPageQuestions = this.publicQuestions;
      return;
    }

    // Get the page number for current page (0-indexed)
    const currentPageNumber = pageNumbers[this.currentPage - 1];

    // Filter questions for current page
    this.currentPageQuestions = this.publicQuestions.filter(
      q => q.pageNumber === currentPageNumber
    );
    this.currentPageChange.emit(this.currentPage);
  }

  private initializeForms() {
    // Initialize user info form
    const userInfoFormControls: { [key: string]: FormControl } = {};
    if (this.userInfoFields && this.userInfoFields.length > 0) {
      this.userInfoFields.forEach((field) => {
        const validators: any[] = [];
        const statusCondition = this.questionnaire?.userInfoCaptureSettings?.statusCondition;

        if (statusCondition === 'MANDATORY') {
          validators.push(Validators.required);
        } else if (statusCondition === 'OPTIONAL' && field.mandatoryFlag) {
          validators.push(Validators.required);
        }

        if (field.fieldType === 'EMAIL') {
          validators.push(...EMAIL_FIELD_VALIDATORS);
        }

        // Max length from fieldLength (text/number input); maxValue overrides when set for text types
        const textTypes = ['TEXT', 'SHORT_TEXT', 'LONG_TEXT', 'FULL_NAME', 'NUMBERS'];
        const isTextType = textTypes.includes(field.fieldType);
        if (isTextType) {
          const minLen = this.parseMinMaxLength(field.minValue);
          const maxLen = this.parseMinMaxLength(field.maxValue) ?? (field.fieldLength != null && field.fieldLength > 0 ? field.fieldLength : null);
          if (minLen != null && minLen > 0) {
            validators.push(Validators.minLength(minLen));
          }
          if (maxLen != null && maxLen > 0) {
            validators.push(Validators.maxLength(maxLen));
          }
        } else if (field.fieldLength != null && field.fieldLength > 0) {
          validators.push(Validators.maxLength(field.fieldLength));
        }

        userInfoFormControls[field.fieldName] = new FormControl(
          { value: '', disabled: false },
          validators
        );
      });
    }
    this.userInfoForm = new FormGroup(userInfoFormControls);

    // Subscribe to form control value changes to clear validation errors
    Object.keys(userInfoFormControls).forEach(fieldName => {
      const control = this.userInfoForm.get(fieldName);
      if (control) {
        control.valueChanges.subscribe(() => {
          if (this.userInfoValidationErrors[fieldName]) {
            delete this.userInfoValidationErrors[fieldName];
          }
        });
      }
    });

    // Initialize questions form
    if (!this.publicQuestions) {
      this.previewForm = new FormGroup({});
      return;
    }
    const formControls: { [key: string]: FormControl } = {};
    this.publicQuestions.forEach((question) => {
      const validators: any[] = [];
      if (question.required) {
        validators.push(Validators.required);
      }
      const opts = question.questionSpecificOptions;
      const subType = question.questionSubType;
      if (question.questionType === QuestionType.TEXT_INPUT && opts) {
        if (subType === QuestionSubType.SHORT_TEXT || subType === QuestionSubType.LONG_TEXT || subType === QuestionSubType.FULL_NAME || subType === QuestionSubType.NUMBERS) {
          const minLen = this.parseMinMaxLength(opts.minValue);
          const maxLen = this.parseMinMaxLength(opts.maxValue);
          if (minLen != null && minLen > 0) {
            validators.push(Validators.minLength(minLen));
          }
          if (maxLen != null && maxLen > 0) {
            validators.push(Validators.maxLength(maxLen));
          }
        }
      }
      formControls[question.questionId] = new FormControl(
        { value: '', disabled: false },
        validators
      );
    });
    this.previewForm = new FormGroup(formControls);
  }

  onAnswerChange(event: any) {
    if (!this.previewForm) return;

    // Store the complete response object for each question
    this.questionAnswers[event.questionId] = event;

    // Update form control with the appropriate value based on question type
    const control = this.previewForm.get(event.questionId);
    if (control) {
      let formValue: any;

      if (event.textResponse !== undefined) {
        // Text box and text area
        formValue = event.textResponse;
        if (formValue) {
          this.focusOnField(event.questionId, false);
        }
      } else if (event.score !== undefined) {
        // NPS and Rating
        formValue = event.score;
        if (formValue) {
          this.focusOnField(event.questionId, false);
        }
      } else if (event.options !== undefined) {
        // Radio, Dropdown, Checkbox
        formValue = event.options.map((option: any) => option.optionId);
        if (
          formValue &&
          formValue.length > 0 &&
          this.publicQuestions?.find((question) => question.questionId === event.questionId)
            ?.questionType !== QuestionType.RANK_ORDER
        ) {
          this.focusOnField(event.questionId, false);
        }
      }

      // Use emitEvent: false to prevent triggering valueChanges in child components
      // The child components already handle their own state updates
      control.setValue(formValue, { emitEvent: false });

      control.markAsTouched();
      // Clear validation error for this field if it exists
      if (this.formErrors[event.questionId]) {
        delete this.formErrors[event.questionId];
      }

      // Manually trigger progress calculation
    }
  }

  validateUserInfoForm(): boolean {
    if (!this.userInfoForm) return false;

    const statusCondition = this.questionnaire?.userInfoCaptureSettings?.statusCondition;

    // ANONYMOUS: No validation needed
    if (statusCondition === 'ANONYMOUS') {
      return true;
    }

    this.showUserInfoValidationErrors = true;
    this.userInfoValidationErrors = {};

    // Check validation based on status condition
    let isValid = true;
    let firstInvalidField: string | null = null;

    this.userInfoFields?.forEach((field) => {
      const control = this.userInfoForm.get(field.fieldName);
      const required = statusCondition === 'MANDATORY' || (statusCondition === 'OPTIONAL' && field.mandatoryFlag);

      if (control && control.invalid && (control.touched || control.dirty || required)) {
        isValid = false;
        if (control.hasError('required')) {
          this.userInfoValidationErrors[field.fieldName] = `${field.fieldName} is required`;
        } else if (control.hasError('email')) {
          this.userInfoValidationErrors[field.fieldName] = 'Please enter a valid email address';
        } 
        // else if (control.hasError('minlength')) {
        //   const err = control.getError('minlength');
        //   this.userInfoValidationErrors[field.fieldName] = `Minimum ${err?.requiredLength ?? field.minValue} ${field.fieldType === 'NUMBERS' ? 'numbers' : 'characters'}`;
        // } else if (control.hasError('maxlength')) {
        //   const err = control.getError('maxlength');
        //   this.userInfoValidationErrors[field.fieldName] = `Maximum ${err?.requiredLength ?? field.maxValue ?? field.fieldLength} ${field.fieldType === 'NUMBERS' ? 'numbers' : 'characters'}`;
        // }
        if (!firstInvalidField) firstInvalidField = field.fieldName;
      }
    });

    // Focus on the first invalid field
    if (!isValid && firstInvalidField) {
      this.focusOnUserInfoField(firstInvalidField);
    }

    return isValid;
  }

  skipUserInfo(): void {
    // Skip user info and go directly to questions
    this.userInfoSubmitted = true;
  }

  validateForm(): boolean {
    if (!this.previewForm) return false;

    this.showValidationErrors = true;
    this.formErrors = {};

    // Only validate questions on current page
    let hasErrors = false;
    let firstInvalidField: string | null = null;

    this.currentPageQuestions?.forEach((question) => {
      const control = this.previewForm.get(question.questionId);
      if (control && control.invalid) {
        hasErrors = true;
        if (control.hasError('required')) {
          this.formErrors[question.questionId] = question.isRequiredValidationMsg;
        }
        else if (control.hasError('minlength')) {
          const err = control.getError('minlength');
          this.formErrors[question.questionId] = `Minimum ${err?.requiredLength ?? question.questionSpecificOptions?.minValue} ${question.questionSubType === QuestionSubType.NUMBERS ? 'numbers' : 'characters'}`;
        } else if (control.hasError('maxlength')) {
          const err = control.getError('maxlength');
          this.formErrors[question.questionId] = `Maximum ${err?.requiredLength ?? question.questionSpecificOptions?.maxValue} ${question.questionSubType === QuestionSubType.NUMBERS ? 'numbers' : 'characters'}`;
        }

        // Mark the first invalid field for focus
        if (!firstInvalidField) {
          firstInvalidField = question.questionId;
        }
      }
    });

    // Focus on the first invalid field
    if (firstInvalidField) {
      this.focusOnField(firstInvalidField, true);
    }

    return !hasErrors;
  }

  validateCurrentPage(): boolean {
    if (!this.previewForm) return false;

    this.showValidationErrors = true;
    this.formErrors = {};

    // Only validate questions on current page
    let hasErrors = false;
    let firstInvalidField: string | null = null;

    this.currentPageQuestions?.forEach((question) => {
      const control = this.previewForm.get(question.questionId);
      if (control && control.invalid) {
        hasErrors = true;
        if (control.hasError('required')) {
          this.formErrors[question.questionId] = question.isRequiredValidationMsg;
        }
        else if (control.hasError('minlength')) {
          const err = control.getError('minlength');
          this.formErrors[question.questionId] = `Minimum ${err?.requiredLength ?? question.questionSpecificOptions?.minValue} ${question.questionSubType === QuestionSubType.NUMBERS ? 'numbers' : 'characters'}`;
        } else if (control.hasError('maxlength')) {
          const err = control.getError('maxlength');
          this.formErrors[question.questionId] = `Maximum ${err?.requiredLength ?? question.questionSpecificOptions?.maxValue} ${question.questionSubType === QuestionSubType.NUMBERS ? 'numbers' : 'characters'}`;
        }
        if (!firstInvalidField) {
          firstInvalidField = question.questionId;
        }
      }
    });

    // Focus on the first invalid field
    if (firstInvalidField) {
      this.focusOnField(firstInvalidField, true);
    }

    return !hasErrors;
  }

  onNextPage(): void {
    // Validate current page before moving to next
    if (!this.validateCurrentPage()) {
      return;
    }

    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updateCurrentPageQuestions();
      // Clear validation errors when moving to next page
      this.clearCurrentPageErrors();
      // Scroll to top of questions
      this.scrollToTop();
    }
  }

  onPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updateCurrentPageQuestions();
      // Clear validation errors when moving to previous page
      this.clearCurrentPageErrors();
      // Scroll to top of questions
      this.scrollToTop();
    }
  }

  private clearCurrentPageErrors(): void {
    // Clear errors only for current page questions
    this.currentPageQuestions?.forEach((question) => {
      if (this.formErrors[question.questionId]) {
        delete this.formErrors[question.questionId];
      }
    });
  }

  private scrollToTop(): void {
    setTimeout(() => {
      const formElement = document.querySelector('form[formGroup]');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  isFirstPage(): boolean {
    return this.currentPage === 1;
  }

  isLastPage(): boolean {
    return this.currentPage === this.totalPages;
  }

  validateAllPages(): boolean {
    if (!this.previewForm || !this.publicQuestions) return false;

    this.showValidationErrors = true;
    this.formErrors = {};

    // Validate all questions across all pages
    let hasErrors = false;
    let firstInvalidQuestion: Question | null = null;

    this.publicQuestions.forEach((question) => {
      const control = this.previewForm.get(question.questionId);
      if (control && control.invalid) {
        hasErrors = true;
        if (control.hasError('required')) {
          this.formErrors[question.questionId] = question.isRequiredValidationMsg;
        }
        else if (control.hasError('minlength')) { 
          const err = control.getError('minlength');
          this.formErrors[question.questionId] = `Minimum ${err?.requiredLength ?? question.questionSpecificOptions?.minValue} ${question.questionSubType === QuestionSubType.NUMBERS ? 'numbers' : 'characters'}`;
        } else if (control.hasError('maxlength')) {
          const err = control.getError('maxlength');
          this.formErrors[question.questionId] = `Maximum ${err?.requiredLength ?? question.questionSpecificOptions?.maxValue} ${question.questionSubType === QuestionSubType.NUMBERS ? 'numbers' : 'characters'}`;
        }
        if (!firstInvalidQuestion) {
          firstInvalidQuestion = question;
        }
      }
    });

    return !hasErrors;
  }

  navigateToFirstErrorPage(): void {
    if (!this.publicQuestions) return;

    // Find the first question with an error
    const firstInvalidQuestion = this.publicQuestions.find(
      q => this.formErrors[q.questionId]
    );

    if (firstInvalidQuestion) {
      // Get unique page numbers sorted
      const pageNumbers = [...new Set(this.publicQuestions.map(q => q.pageNumber))].sort((a, b) => a - b);
      const targetPageIndex = pageNumbers.indexOf(firstInvalidQuestion.pageNumber);

      if (targetPageIndex >= 0) {
        this.currentPage = targetPageIndex + 1;
        this.updateCurrentPageQuestions();

        // Scroll to the invalid question after navigation
        setTimeout(() => {
          this.focusOnField(firstInvalidQuestion.questionId, true);
        }, 100);
      }
    }
  }
  private focusOnUserInfoField(fieldName: string) {
    setTimeout(() => {
      const element = document.querySelector(`[data-user-info-field="${fieldName}"]`);
      if (element) {
        const inputElement = element.querySelector('input, textarea, select') as HTMLElement;
        if (inputElement) {
          inputElement.focus();
          inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 10);
  }

  private focusOnField(questionId: string, isError: boolean = false) {
    // Use setTimeout to ensure DOM is updated
    // setTimeout(() => {
    const element = document.querySelector(`[data-question-id="${questionId}"]`);
    if (element) {
      // First try to find the question order/title element
      const questionOrderElement = element.querySelector(
        '.question-number, .question-title, [data-question-order]'
      ) as HTMLElement;
      if (questionOrderElement) {
        if (isError) {
          questionOrderElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        // Fallback to input element if question order not found
        const inputElement = element.querySelector('input, textarea, select') as HTMLElement;
        if (inputElement) {
          inputElement.focus();
          inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
    // }, 10);
  }
  // Success state

  resetForm() {
    if (this.previewForm) {
      this.previewForm.reset();
      this.questionAnswers = {};
      this.formErrors = {};
      this.showValidationErrors = false;
    }
    if (this.userInfoForm) {
      this.userInfoForm.reset();
      this.userInfoAnswers = {};
      this.userInfoValidationErrors = {};
      this.showUserInfoValidationErrors = false;
      this.userInfoSubmitted = false;
    }
  }

  getDropdownOptions(options: QuestionOption[] = []): { value: string; label: string }[] {
    return options
      .sort((a, b) => a.order - b.order)
      .map((opt) => ({
        value: opt.optionId,
        label: opt.text,
      }));
  }
  getQuestionControl(questionId: string): FormControl {
    const control = this.previewForm.get(questionId) as FormControl;
    if (!control) {
      // Create a default control if it doesn't exist
      const defaultControl = new FormControl('');
      this.previewForm.addControl(questionId, defaultControl);
      return defaultControl;
    }
    return control;
  }
  isQuestionInvalid(questionId: string): boolean {
    const control = this.getQuestionControl(questionId);
    return this.showValidationErrors && control ? control.invalid && control.touched : false;
  }

  getQuestionErrorMessage(questionId: string): string {
    return this.formErrors[questionId] || '';
  }

  getQuestionOrder(questionId: string): number {
    const questionIndex = this.publicQuestions?.findIndex((q) => q.questionId === questionId);
    return questionIndex !== undefined && questionIndex >= 0 ? questionIndex + 1 : 0;
  }

  getQuestionOrderText(questionId: string): string {
    const order = this.getQuestionOrder(questionId);
    return order > 0 ? `Question ${order}` : '';
  }

  /**
   * Get the starting question number for the current page
   * This is the sum of all questions on previous pages
   */
  getCurrentPageStartQuestionNumber(): number {
    if (!this.publicQuestions || this.currentPage === 1) {
      return 0;
    }

    // Get unique page numbers sorted
    const pageNumbers = [...new Set(this.publicQuestions.map(q => q.pageNumber))].sort((a, b) => a - b);

    let totalQuestions = 0;
    // Sum questions from all previous pages
    for (let i = 0; i < this.currentPage - 1; i++) {
      const pageNumber = pageNumbers[i];
      const questionsOnPage = this.publicQuestions.filter(q => q.pageNumber === pageNumber);
      totalQuestions += questionsOnPage.length;
    }

    return totalQuestions;
  }

  /**
   * Get the global question number (continuous across all pages)
   * @param index - The index of the question in the current page
   * @returns The global question number
   */
  getGlobalQuestionNumber(index: number): number {
    return this.getCurrentPageStartQuestionNumber() + index + 1;
  }

  getUserInfoControl(fieldName: string): FormControl {
    const control = this.userInfoForm.get(fieldName) as FormControl;
    if (!control) {
      const defaultControl = new FormControl('');
      this.userInfoForm.addControl(fieldName, defaultControl);
      return defaultControl;
    }
    return control;
  }

  isUserInfoFieldInvalid(fieldName: string): boolean {
    const control = this.getUserInfoControl(fieldName);
    return this.showUserInfoValidationErrors && control ? control.invalid && control.touched : false;
  }

  getUserInfoFieldErrorMessage(fieldName: string): string {
    return this.userInfoValidationErrors[fieldName] || '';
  }

  getUserInfoInputType(fieldType: string): 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'time' | 'datetime-local' | 'month' | 'week' {
    switch (fieldType) {
      case 'EMAIL':
        return 'email';
      case 'PHONE':
        return 'tel';
      case 'NUMBERS':
        return 'number';
      case 'WEBSITE_URL':
        return 'url';
      case 'CALENDAR':
        return 'date';
      default:
        return 'text';
    }
  }

  getUserInfoQuestion(field: UserInfoField): Question | null {
    // Create a mock question object for user info fields to work with question components
    // Use fieldName as the label/question text
    const statusCondition = this.questionnaire?.userInfoCaptureSettings?.statusCondition;

    return {
      pageNumber: 0,
      questionId: field.fieldName,
      question: field.fieldName, // fieldName is used as label
      kpiFlag: false,
      followUpQuestionFlag: false,
      followUpQuestions: [],
      isRequiredValidationMsg: `${field.fieldName} is required`,
      questionType: 'TEXT_INPUT',
      questionSubType: 'SHORT_TEXT',
      order: 0,
      regExpression: '',
      questionSpecificOptions: {} as any,
      questionOptions: [],
      questionConditions: [],
      // Only required if MANDATORY status or field has mandatoryFlag = true
      required: statusCondition === 'MANDATORY' || field.mandatoryFlag
    };
  }

  getUserInfoStatusCondition(): string {
    return this.questionnaire?.userInfoCaptureSettings?.statusCondition || 'ANONYMOUS';
  }

  /** Parse minValue/maxValue as length (for SHORT_TEXT, LONG_TEXT, FULL_NAME) */
  parseMinMaxLength(value: string | number | undefined): number | null {
    if (value == null || value === '') return null;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }


  isUserInfoOptional(): boolean {
    return this.getUserInfoStatusCondition() === 'OPTIONAL';
  }

  onUserInfoChange(fieldName: string, event: any) {
    const control = this.userInfoForm.get(fieldName);
    if (control) {
      // Extract value from event (could be textResponse or direct value)
      const value = event?.textResponse !== undefined ? event.textResponse : (event?.target?.value || event);
      control.setValue(value);
      control.markAsTouched();

      // Clear validation error for this field if it exists
      if (this.userInfoValidationErrors[fieldName]) {
        delete this.userInfoValidationErrors[fieldName];
      }
    }
  }

  /**
   * Check if welcome screen exists and has content
   * @returns true if welcome screen has any content (logo, title, description, or button)
   */
  hasWelcomeScreen(): boolean {
    if (!this.questionnaire?.welcomeScreenTemplate) {
      return false;
    }

    const template = this.questionnaire.welcomeScreenTemplate;
    return !!(
      template.logo?.logoImage ||
      template.title?.text ||
      template.description?.text ||
      template.button?.name
    );
  }

}
